import useAuth from 'hooks/UseAuth';
import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Header } from 'shared/components/organisms/Header/Header';
import { Sidebar } from 'shared/components/organisms/Sidebar/Sidebar';
import { InputIconBox } from 'shared/components/molecules/InputIconBox/InputIconBox';
import { IconInput } from 'shared/components/molecules/IconInput/IconInput';
import { SelectIcon } from 'shared/components/molecules/SelectIcon/SelectIcon';
import { ProfileImage } from 'shared/components/organisms/ProfileImage/ProfileImage';
import {
  OptionValues,
  SelectBox,
} from 'shared/components/molecules/SelectBox/SelectBox';

import { ReactComponent as LeftArrow } from 'assets/icons/left-arrow.svg';
import filled_man from 'assets/icons/filled_man.svg';
import gray_filled_man from 'assets/icons/gray_filled_man.svg';

import { handleInputChange } from './utils/HandleInputChange';
import { cnpjFormat, sanitizeString } from 'utils/format';

import api from 'services/api';

import {
  CreditCardIcon,
  CertifiedIcon,
  Container,
  Content,
  FileUploadIcon,
  Section,
  Subheader,
  CPFIcon,
} from './_proProfile';

import { IBankFormData, IProProfileFormData, ISelectItem } from './interfaces';

export const ProProfile = () => {
  const { user } = useAuth();
  const userCNPJ = user?.legal_person?.cnpj;
  const providerId = user?.provider?.id;
  const serviceType = user?.provider?.service_types[0];

  const [serviceTypes, setServiceTypes] = useState([] as Array<OptionValues>);
  const [selectedServiceType, setSelectedServiceType] = useState({
    label: serviceType?.name || '',
    value: serviceType?.id.toString() || '',
  } as OptionValues);

  const [bankCodes, setBankCodes] = useState([] as Array<OptionValues>);
  const [selectedBankCode, setSelectedBankCode] = useState({} as OptionValues);

  const [accountTypes, setAccountTypes] = useState([] as Array<OptionValues>);
  const [selectedAccountType, setSelectedAccountType] = useState(
    {} as OptionValues,
  );

  const [bankAgency, setBankAgency] = useState('');
  const [bankAccount, setBankAccount] = useState('');

  const [ProProfileFormData, setProProfileFormData] = useState({
    cnpj: userCNPJ ? cnpjFormat(userCNPJ) : '',
  } as IProProfileFormData);

  const [bankFormData, setBankFormData] = useState({} as IBankFormData);

  const handleFormSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    try {
      const promises = [];

      if (selectedServiceType.value !== `${serviceType.id}`) {
        const newServiceType = {
          services_types: [
            {
              id: selectedServiceType.value,
              price: 9000,
            },
          ],
        };

        promises.push(
          api.put(`/providers/${providerId}/service-types`, newServiceType),
        );
      }

      if (bankFormData?.id) {
        const { id, ...bankRest } = bankFormData;

        const bankPayload = {
          ...bankRest,
          bank_code_id: selectedBankCode.value,
          bank_account_type_id: selectedAccountType.value,
        };

        promises.push(api.put(`/banks/${id}`, bankPayload));
      }

      if (sanitizeString(ProProfileFormData.cnpj) !== userCNPJ) {
        const payload = {
          legal_person: {
            cnpj: sanitizeString(ProProfileFormData.cnpj) || undefined,
          },
        };

        promises.push(api.put('/users', payload));
      }

      if (promises?.length > 0) {
        await Promise.all(promises);

        toast.success('Alterações salvas com sucesso!');
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const ensureServiceTypes = async () => {
    try {
      // const response: any = await api.get('/service-types')

      // const results = response?.data?.results.filter((category: any) => category?.is_active)

      const results =
        user?.provider?.service_types.map((serviceType) => ({
          ...serviceType,
          id: serviceType.id.toString(),
        })) || [];

      setServiceTypes(
        results.map((serviceType: ISelectItem) => ({
          label: serviceType?.name,
          value: serviceType?.id,
        })),
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const ensureBanks = async () => {
    try {
      const response: any = await api.get('/bank-codes?limit=214');

      const supportedCodeBanks = [
        '237',
        '001',
        '341',
        '260',
        '197',
        '104',
        '033',
        '077',
        '336',
        '208',
        '422',
      ];

      const filteredBanks = response?.data?.results.filter(
        (bank: IBankFormData) => supportedCodeBanks.includes(bank.code),
      );

      setBankCodes(
        filteredBanks.map((bankCode: ISelectItem) => ({
          label: `${bankCode?.code} - ${bankCode?.name}`,
          value: bankCode?.id,
        })),
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const ensureAccountTypes = async () => {
    try {
      const response: any = await api.get('/bank-account-types');

      const results = response?.data?.results;

      setAccountTypes(
        results.map((accountType: ISelectItem) => ({
          label: accountType?.name,
          value: accountType?.id,
        })),
      );
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  const ensureBankData = async () => {
    try {
      const response: any = await api.get('/banks');

      const bankData = response?.data?.results[0];

      if (!bankData) {
        return;
      }

      const {
        status,
        status_id,
        updated_at,
        created_at,
        user_id,
        bank_code,
        agency_check_digit,
        bank_code: { code, name },
        bank_account_type,
        bank_account_type_id,
        bank_code_id,
        ...bankRest
      } = bankData;

      setSelectedAccountType({
        label: bank_account_type?.name,
        value: bank_account_type_id,
      });

      setSelectedBankCode({
        label: `${code} - ${name}`,
        value: bank_code_id,
      });

      setBankFormData((prevState) => ({
        ...prevState,
        ...bankRest,
      }));
    } catch (error: any) {
      toast.error(error?.response?.data?.message);
    }
  };

  useEffect(() => {
    ensureServiceTypes();
    ensureBanks();
    ensureAccountTypes();
    ensureBankData();
  }, []);

  return (
    <Container>
      <Header />
      <Sidebar />
      <Subheader>
        <Link to="/perfil" className="personal-profile__subheader-button">
          <LeftArrow className="personal-profile__subheader-icon" />
        </Link>
      </Subheader>

      <Content>
        <ProfileImage />

        <Section>
          <h1 className="pro-profile__section__title">
            Meus dados profissionais
          </h1>
          <form
            onSubmit={handleFormSubmit}
            className="pro-profile__section__form"
          >
            <div className="pro-profile__section__form-wrapper">
              <label className="pro-profile__section__form-label" htmlFor="">
                Categorias de Serviços
              </label>
              <div className="pro-profile__section__form-select">
                <SelectBox
                  placeholder="Selecione"
                  selectedItem={selectedServiceType}
                  setSelectedItem={setSelectedServiceType}
                  options={serviceTypes}
                  borderRadius="19px"
                />
              </div>
              <label className="pro-profile__section__form-label" htmlFor="">
                CNPJ
              </label>
              <IconInput
                icon={gray_filled_man}
                activeIcon={filled_man}
                placeholder={'CNPJ'}
                inputValue={cnpjFormat(ProProfileFormData.cnpj)}
                setInputValue={(text: string) => {
                  setProProfileFormData((prevState) => ({
                    ...prevState,
                    cnpj: text,
                  }));
                }}
                className="pro-profile__section__form-icon-input"
              />
              {bankFormData?.id && (
                <>
                  <label className="pro-profile__section__form-label">
                    Dados Bancários
                  </label>
                  <div className="pro-profile__section__form-select">
                    <SelectIcon
                      icon={<CreditCardIcon />}
                      placeholder="Selecione"
                      selectedItem={selectedBankCode}
                      setSelectedItem={setSelectedBankCode}
                      options={bankCodes}
                      borderRadius="19px"
                    />
                  </div>
                  <div className="pro-profile__section__account-wrapper">
                    <div className="pro-profile__section__account-select">
                      <SelectBox
                        placeholder="Selecione"
                        selectedItem={selectedAccountType}
                        setSelectedItem={setSelectedAccountType}
                        options={accountTypes}
                        borderRadius="19px"
                      />
                    </div>
                    <input
                      className="pro-profile__section__account-input"
                      type="text"
                      name="agency"
                      onChange={(event) =>
                        handleInputChange({ event, setState: setBankFormData })
                      }
                      value={bankFormData?.agency}
                      placeholder="Ag: 0001"
                    />
                  </div>
                  <input
                    className="pro-profile__section__form-input"
                    type="text"
                    name="account"
                    onChange={(event) =>
                      handleInputChange({ event, setState: setBankFormData })
                    }
                    value={bankFormData?.account}
                    placeholder="Conta: 4321-0"
                  />
                </>
              )}
              <button
                type="submit"
                className="pro-profile__section__submit-button"
              >
                Salvar alteração
              </button>
            </div>
          </form>
        </Section>
      </Content>
    </Container>
  );
};
